import { getAppConfig } from '@services/config';
import { apiHttpService } from '@services/http';
import { CACHE_TAGS } from '@services/http/http';

import { TradeInEligibilityContext, sapicTradeInRepository } from '@repositories/sapic-trade-in';

import { EasyTradeInPhoneInfo, TradeInSubType } from '@app-types/easy-trade-in';
import {
    RecommerceCriteriaType,
    RecommerceProductsType,
    RecommerceQuoteType,
    TradeInAutocompleteType,
    TradeInCriteriaResponsesType,
    TradeInCriteriaType,
    TradeInQuoteType,
} from '@app-types/trade-in';
import type { UserType } from '@app-types/user';

export type PartnerProductInfoType = {
    phoneInfo: EasyTradeInPhoneInfo;
    questions: TradeInCriteriaType;
};

export type { TradeInEligibilityContext } from '@repositories/sapic-trade-in';

const appConfig = getAppConfig();

export const getEasyTradeInEligibility = (context: TradeInEligibilityContext) =>
    sapicTradeInRepository.getTradeInEligibility(context);

export const submitTradeIn = (
    context: TradeInEligibilityContext,
    partnerRequestId: string,
    productSubtype: TradeInSubType,
) => sapicTradeInRepository.submitTradeIn(context, partnerRequestId, productSubtype);

export const getPartnerProducts = async (
    productName: string,
    abortController: AbortController,
    user: UserType | null,
): Promise<TradeInAutocompleteType[]> => {
    if (productName.length < 3) {
        return [];
    }
    const payload = {
        controller: abortController,
        ttl: Number.parseInt(getAppConfig().cache.ttl.productDetail),
        tags: [CACHE_TAGS.PRODUCT_DETAIL],
        ...(user ? { user } : {}),
    };
    return apiHttpService
        .get<RecommerceProductsType>(
            `${appConfig.b2r.url}/produits-partenaires?${new URLSearchParams({ libelle: productName })}`,
            undefined,
            payload,
        )
        .then((res) => {
            return res.items.map((item) => {
                return {
                    label: item.nom,
                    brand: item.marque,
                    id: item.id,
                };
            });
        })
        .catch(() => []);
};

export const getPartnerProduct = async (
    productId: string,
    user: UserType | null = null,
): Promise<PartnerProductInfoType> => {
    const payload = {
        ttl: Number.parseInt(getAppConfig().cache.ttl.productDetail),
        tags: [CACHE_TAGS.PRODUCT_DETAIL],
        ...(user ? { user } : {}),
    };
    return apiHttpService
        .get<RecommerceCriteriaType>(`${appConfig.b2r.url}/produits-partenaires/${productId}`, undefined, payload)
        .then((res) => ({
            phoneInfo: {
                id: res.id,
                name: res.nom,
                brand: res.marque,
            },
            questions: res.criteresRepriseProduit.map((item) => ({
                id: item.id,
                label: item.libelle,
                reponses: item.reponses.map((reponse) => ({
                    description: reponse.description,
                    id: reponse.id,
                    label: reponse.libelle,
                })),
            })),
        }));
};

export const getPartnerProductTradeInQuestions = async (
    productId: string,
    user: UserType | null = null,
): Promise<TradeInCriteriaType> => {
    return getPartnerProduct(productId, user).then((product) => product.questions);
};

export const getPartnerProductTradeInQuote = async (
    productId: string,
    request:
        | {
              mode: 'STANDARD';
              criteria: TradeInCriteriaResponsesType['questions'];
          }
        | {
              mode: 'GARANTIE';
              subscribedFundingId: string;
              criteria: TradeInCriteriaResponsesType['questions'];
          },
    user: UserType | null,
    abortController?: AbortController,
): Promise<TradeInQuoteType> => {
    const { mode, criteria } = request;
    const payload = {
        ...(user ? { user } : {}),
        ...(abortController ? { controller: abortController } : {}),
    };

    return apiHttpService
        .post<RecommerceQuoteType>(
            `${appConfig.b2r.url}/produits-partenaires/${productId}/devis`,
            {
                mode,
                ...('subscribedFundingId' in request && {
                    idFinancementSouscrit: request.subscribedFundingId,
                }),
                criteresReprise: criteria,
            },
            payload,
        )
        .then(({ devis: [quote] }) => {
            if (!quote) {
                throw new Error('Could not find a quote in the trade-in response');
            }

            return {
                id: quote.id,
                delivery: quote.livraison.map((livraison) => ({
                    deliveryMode: livraison.modeLivraison,
                    insuranceAmount: livraison.montantAssurance,
                    deliveryAmount: livraison.montantLivraison,
                })),
                estimatedRecoveryAmount: quote.montantEstimeReprise,
            };
        });
};
